// src/components/charts/GenericPieChart.tsx
import React, { useState } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Sector } from 'recharts';
import { PieChartDataItem } from '../../interfaces/analytics';
import { NoDataPlaceholder } from '../common/NoDataPlaceholder';


// This shape renderer can be kept here or passed as a prop for more flexibility
const renderActiveShape = (props: any) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
  } = props;
  const value = payload?.value || payload?.studies || 0;
  return (
    <g>
      <text
        x={cx}
        y={cy - 8}
        dy={8}
        textAnchor="middle"
        fill="#111827"
        className="font-bold text-xl"
      >
        {value?.toLocaleString() ?? 0}
      </text>
      <text
        x={cx}
        y={cy + 12}
        dy={8}
        textAnchor="middle"
        fill="#6B7280"
        className="text-xs"
      >
        {payload.name}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 4}
        outerRadius={outerRadius + 8}
        fill={fill}
      />
    </g>
  );
};

interface GenericPieChartProps {
  data: PieChartDataItem[];
  colors: string[];
}

export const GenericPieChart: React.FC<GenericPieChartProps> = ({ data, colors }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!data || data.length === 0) {
    return <NoDataPlaceholder className="h-64" />;
  }

  return (
    <>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={75}
              dataKey="value"
              onMouseEnter={(_, index) => setActiveIndex(index)}
              paddingAngle={5}
            >
              {data.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 space-y-2">
        {data.map((entry, index) => (
          <div key={entry.name} className="flex justify-between items-center text-xs">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors[index] }} />
              <span className="text-gray-600 dark:text-gray-400">{entry.name}</span>
            </div>
            <span className="font-semibold text-gray-800 dark:text-gray-200">{entry.value.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </>
  );
};